import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    Chip,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    FormControlLabel,
    Alert,
    CircularProgress,
    Divider,
    Autocomplete
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    LocalOffer as PromoIcon
} from '@mui/icons-material';
import {
    PRICING_PLAN_TYPES,
    getPricingPlans,
    createPricingPlan,
    updatePricingPlan,
    deletePricingPlan,
    createPricingTier,
    updatePricingTier,
    deletePricingTier,
    getPromoCodes,
    createPromoCode,
    updatePromoCode,
    deletePromoCode,
    getPermissions
} from '../Utils/pricingApi';

const emptyPlanForm = {
    type: 'fixed',
    name: '',
    currency: 'EUR',
    base_amount: '',
    annual_amount: '',
    quantity_limit: '',
    dimension: '',
    starts_at: '',
    ends_at: '',
    priority: 0,
    is_active: true,
    configText: '{}',
    authorizations: [],
    highlighted_permissions: []
};

const emptyTierForm = { dimension: 'quantity', min_value: '', max_value: '', unit_price: '', flat_fee: '', label: '' };

const emptyPromoForm = {
    code: '', discount_type: 'percentage', discount_value: '',
    starts_at: '', ends_at: '', max_uses: '', max_uses_per_user: '', is_active: true
};

const toDateInput = (iso) => (iso ? iso.substring(0, 10) : '');
const toIsoOrNull = (dateStr) => (dateStr ? new Date(dateStr).toISOString() : null);

export default function PricingPlansPanel({ accessToken, priceableType, priceableId, applicationId, disabled = false }) {
    const theme = useTheme();
    // Les inputs <input type="date"> natifs ignorent le thème MUI : sans ça, Chrome affiche
    // l'icône de calendrier avec un fond clair (illisible/moche en thème sombre).
    const dateInputSx = { '& input': { colorScheme: theme.palette.mode } };

    // Le catalogue de permissions est toujours rattaché à l'application (pas à l'option) :
    // applicationId doit être fourni par le parent même pour un panneau d'option.
    const permissionScopeId = applicationId || (priceableType === 'application' ? priceableId : null);

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [permissionsCatalog, setPermissionsCatalog] = useState([]);

    const [planDialogOpen, setPlanDialogOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [planForm, setPlanForm] = useState(emptyPlanForm);
    const [planFormError, setPlanFormError] = useState('');

    const [tierDialogOpen, setTierDialogOpen] = useState(false);
    const [tierPlanId, setTierPlanId] = useState(null);
    const [editingTier, setEditingTier] = useState(null);
    const [tierForm, setTierForm] = useState(emptyTierForm);

    const [promoDialogOpen, setPromoDialogOpen] = useState(false);
    const [promoPlanId, setPromoPlanId] = useState(null);
    const [editingPromo, setEditingPromo] = useState(null);
    const [promoForm, setPromoForm] = useState(emptyPromoForm);

    const fetchPlans = useCallback(() => {
        if (!accessToken || !priceableId) return;
        setLoading(true);
        getPricingPlans(accessToken, priceableType, priceableId)
            .then(setPlans)
            .catch(() => setError('Impossible de charger les plans tarifaires'))
            .finally(() => setLoading(false));
    }, [accessToken, priceableType, priceableId]);

    useEffect(() => { fetchPlans(); }, [fetchPlans]);

    useEffect(() => {
        if (!accessToken || !permissionScopeId) return;
        getPermissions(accessToken, permissionScopeId).then(setPermissionsCatalog).catch(() => setPermissionsCatalog([]));
    }, [accessToken, permissionScopeId]);

    // ── Plan CRUD ──────────────────────────────────────────────────────────

    const openCreatePlan = () => {
        setEditingPlan(null);
        setPlanForm(emptyPlanForm);
        setPlanFormError('');
        setPlanDialogOpen(true);
    };

    const openEditPlan = (plan) => {
        setEditingPlan(plan);
        setPlanForm({
            type: plan.type,
            name: plan.name,
            currency: plan.currency || 'EUR',
            base_amount: plan.base_amount ?? '',
            annual_amount: plan.annual_amount ?? '',
            quantity_limit: plan.quantity_limit ?? '',
            dimension: plan.dimension || '',
            starts_at: toDateInput(plan.starts_at),
            ends_at: toDateInput(plan.ends_at),
            priority: plan.priority ?? 0,
            is_active: plan.is_active,
            configText: JSON.stringify(plan.config || {}, null, 2),
            authorizations: Array.isArray(plan.authorizations) ? plan.authorizations : [],
            highlighted_permissions: Array.isArray(plan.highlighted_permissions) ? plan.highlighted_permissions : []
        });
        setPlanFormError('');
        setPlanDialogOpen(true);
    };

    const handleSavePlan = () => {
        let config;
        try {
            config = planForm.configText.trim() ? JSON.parse(planForm.configText) : {};
        } catch (e) {
            setPlanFormError('Le champ "Configuration" doit être un JSON valide');
            return;
        }

        const payload = {
            priceable_type: priceableType,
            priceable_id: String(priceableId),
            type: planForm.type,
            name: planForm.name,
            currency: planForm.currency,
            base_amount: planForm.base_amount === '' ? null : parseFloat(planForm.base_amount),
            annual_amount: planForm.annual_amount === '' ? null : parseFloat(planForm.annual_amount),
            quantity_limit: planForm.quantity_limit === '' ? null : parseFloat(planForm.quantity_limit),
            dimension: planForm.dimension.trim() || null,
            starts_at: toIsoOrNull(planForm.starts_at),
            ends_at: toIsoOrNull(planForm.ends_at),
            priority: parseInt(planForm.priority, 10) || 0,
            is_active: planForm.is_active,
            config,
            authorizations: planForm.authorizations,
            // Can't highlight something that isn't even granted by the plan.
            highlighted_permissions: planForm.highlighted_permissions.filter(
                (key) => planForm.authorizations.includes(key)
            )
        };

        const req = editingPlan
            ? updatePricingPlan(accessToken, editingPlan.id, payload)
            : createPricingPlan(accessToken, payload);

        req.then(() => { setPlanDialogOpen(false); fetchPlans(); })
           .catch(err => setPlanFormError(err.response?.data?.message || 'Erreur lors de l\'enregistrement'));
    };

    const handleDeletePlan = (plan) => {
        if (!window.confirm(`Supprimer le plan tarifaire "${plan.name}" ?`)) return;
        deletePricingPlan(accessToken, plan.id).then(fetchPlans).catch(() => setError('Suppression impossible'));
    };

    // ── Tier CRUD ──────────────────────────────────────────────────────────

    const openCreateTier = (planId) => {
        setTierPlanId(planId);
        setEditingTier(null);
        setTierForm(emptyTierForm);
        setTierDialogOpen(true);
    };

    const openEditTier = (planId, tier) => {
        setTierPlanId(planId);
        setEditingTier(tier);
        setTierForm({
            dimension: tier.dimension,
            min_value: tier.min_value,
            max_value: tier.max_value ?? '',
            unit_price: tier.unit_price,
            flat_fee: tier.flat_fee ?? '',
            label: tier.label ?? ''
        });
        setTierDialogOpen(true);
    };

    const handleSaveTier = () => {
        const payload = {
            dimension: tierForm.dimension,
            min_value: parseFloat(tierForm.min_value) || 0,
            max_value: tierForm.max_value === '' ? null : parseFloat(tierForm.max_value),
            unit_price: parseFloat(tierForm.unit_price) || 0,
            flat_fee: tierForm.flat_fee === '' ? null : parseFloat(tierForm.flat_fee),
            label: tierForm.label
        };

        const req = editingTier
            ? updatePricingTier(accessToken, tierPlanId, editingTier.id, payload)
            : createPricingTier(accessToken, tierPlanId, payload);

        req.then(() => { setTierDialogOpen(false); fetchPlans(); }).catch(() => setError('Erreur lors de l\'enregistrement du palier'));
    };

    const handleDeleteTier = (planId, tier) => {
        if (!window.confirm('Supprimer ce palier ?')) return;
        deletePricingTier(accessToken, planId, tier.id).then(fetchPlans).catch(() => setError('Suppression impossible'));
    };

    // ── Promo code CRUD ────────────────────────────────────────────────────

    const openCreatePromo = (planId) => {
        setPromoPlanId(planId);
        setEditingPromo(null);
        setPromoForm(emptyPromoForm);
        setPromoDialogOpen(true);
    };

    const openEditPromo = (planId, promo) => {
        setPromoPlanId(planId);
        setEditingPromo(promo);
        setPromoForm({
            code: promo.code,
            discount_type: promo.discount_type,
            discount_value: promo.discount_value,
            starts_at: toDateInput(promo.starts_at),
            ends_at: toDateInput(promo.ends_at),
            max_uses: promo.max_uses ?? '',
            max_uses_per_user: promo.max_uses_per_user ?? '',
            is_active: promo.is_active
        });
        setPromoDialogOpen(true);
    };

    const handleSavePromo = () => {
        const payload = {
            code: promoForm.code.trim().toUpperCase(),
            pricing_plan_id: promoPlanId,
            discount_type: promoForm.discount_type,
            discount_value: parseFloat(promoForm.discount_value) || 0,
            starts_at: toIsoOrNull(promoForm.starts_at),
            ends_at: toIsoOrNull(promoForm.ends_at),
            max_uses: promoForm.max_uses === '' ? null : parseInt(promoForm.max_uses, 10),
            max_uses_per_user: promoForm.max_uses_per_user === '' ? null : parseInt(promoForm.max_uses_per_user, 10),
            is_active: promoForm.is_active
        };

        const req = editingPromo
            ? updatePromoCode(accessToken, editingPromo.id, payload)
            : createPromoCode(accessToken, payload);

        req.then(() => { setPromoDialogOpen(false); fetchPlans(); }).catch(() => setError('Erreur lors de l\'enregistrement du code promo'));
    };

    const handleDeletePromo = (promo) => {
        if (!window.confirm(`Supprimer le code promo "${promo.code}" ?`)) return;
        deletePromoCode(accessToken, promo.id).then(fetchPlans).catch(() => setError('Suppression impossible'));
    };

    const typeLabel = (type) => PRICING_PLAN_TYPES.find(t => t.value === type)?.label || type;
    const showTiers = (type) => ['tiered', 'volume_grid'].includes(type);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>;
    }

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                {!disabled && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreatePlan}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        Nouveau plan tarifaire
                    </Button>
                )}
            </Box>

            {plans.length === 0 ? (
                <Alert severity="info">Aucun plan tarifaire configuré pour le moment.</Alert>
            ) : (
                <Grid container spacing={2}>
                    {plans.map((plan) => (
                        <Grid item xs={12} key={plan.id}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="h6" sx={{ color: 'primary.main' }}>{plan.name}</Typography>
                                                <Chip label={typeLabel(plan.type)} size="small" />
                                                <Chip label={plan.is_active ? 'Actif' : 'Inactif'} size="small"
                                                    color={plan.is_active ? 'success' : 'default'} />
                                                {plan.priority > 0 && <Chip label={`Priorité ${plan.priority}`} size="small" variant="outlined" />}
                                            </Box>
                                            {plan.base_amount !== null && (
                                                <Typography variant="body1">
                                                    {plan.base_amount} {plan.currency}
                                                    {plan.annual_amount != null && ` / mois · ${plan.annual_amount} ${plan.currency} / an`}
                                                </Typography>
                                            )}
                                            {plan.quantity_limit != null && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Pack : {Number(plan.quantity_limit).toLocaleString('fr-FR')} {plan.dimension || ''} / mois
                                                </Typography>
                                            )}
                                            {(plan.starts_at || plan.ends_at) && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {plan.starts_at ? `Du ${toDateInput(plan.starts_at)}` : ''}
                                                    {plan.ends_at ? ` au ${toDateInput(plan.ends_at)}` : ''}
                                                </Typography>
                                            )}
                                            {plan.authorizations?.length > 0 && (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                    {plan.authorizations.map((auth) => (
                                                        <Chip
                                                            key={auth}
                                                            label={auth}
                                                            size="small"
                                                            color="primary"
                                                            variant={plan.highlighted_permissions?.includes(auth) ? 'filled' : 'outlined'}
                                                            title={plan.highlighted_permissions?.includes(auth) ? 'Mise en avant sur la carte' : 'Visible seulement dans la grille de comparaison'}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                        {!disabled && (
                                            <Box>
                                                <IconButton size="small" onClick={() => openEditPlan(plan)}><EditIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDeletePlan(plan)}><DeleteIcon fontSize="small" /></IconButton>
                                            </Box>
                                        )}
                                    </Box>

                                    {plan.config && Object.keys(plan.config).length > 0 && (
                                        <Typography variant="caption" component="pre" sx={{
                                            display: 'block', bgcolor: 'action.hover', p: 1, borderRadius: 1,
                                            fontFamily: 'monospace', whiteSpace: 'pre-wrap', mb: 1
                                        }}>
                                            {JSON.stringify(plan.config)}
                                        </Typography>
                                    )}

                                    {showTiers(plan.type) && (
                                        <Box sx={{ mt: 2 }}>
                                            <Divider sx={{ mb: 1.5 }} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="subtitle2">Paliers / grille tarifaire</Typography>
                                                {!disabled && (
                                                    <Button size="small" startIcon={<AddIcon />} onClick={() => openCreateTier(plan.id)}>
                                                        Ajouter un palier
                                                    </Button>
                                                )}
                                            </Box>
                                            {plan.Tiers?.length > 0 ? (
                                                <TableContainer component={Paper} variant="outlined">
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Dimension</TableCell>
                                                                <TableCell>Min</TableCell>
                                                                <TableCell>Max</TableCell>
                                                                <TableCell>Prix unitaire</TableCell>
                                                                <TableCell>Frais fixe</TableCell>
                                                                <TableCell>Libellé</TableCell>
                                                                {!disabled && <TableCell>Actions</TableCell>}
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {plan.Tiers.map((tier) => (
                                                                <TableRow key={tier.id}>
                                                                    <TableCell>{tier.dimension}</TableCell>
                                                                    <TableCell>{tier.min_value}</TableCell>
                                                                    <TableCell>{tier.max_value ?? '∞'}</TableCell>
                                                                    <TableCell>{tier.unit_price} {plan.currency}</TableCell>
                                                                    <TableCell>{tier.flat_fee ?? '—'}</TableCell>
                                                                    <TableCell>{tier.label || '—'}</TableCell>
                                                                    {!disabled && (
                                                                        <TableCell>
                                                                            <IconButton size="small" onClick={() => openEditTier(plan.id, tier)}><EditIcon fontSize="small" /></IconButton>
                                                                            <IconButton size="small" color="error" onClick={() => handleDeleteTier(plan.id, tier)}><DeleteIcon fontSize="small" /></IconButton>
                                                                        </TableCell>
                                                                    )}
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">Aucun palier défini.</Typography>
                                            )}
                                        </Box>
                                    )}

                                    <Box sx={{ mt: 2 }}>
                                        <Divider sx={{ mb: 1.5 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle2">
                                                <PromoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                                Codes promo
                                            </Typography>
                                            {!disabled && (
                                                <Button size="small" startIcon={<AddIcon />} onClick={() => openCreatePromo(plan.id)}>
                                                    Ajouter un code
                                                </Button>
                                            )}
                                        </Box>
                                        {plan.PromoCodes?.length > 0 ? (
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Code</TableCell>
                                                            <TableCell>Remise</TableCell>
                                                            <TableCell>Période</TableCell>
                                                            <TableCell>Usage</TableCell>
                                                            <TableCell>Statut</TableCell>
                                                            {!disabled && <TableCell>Actions</TableCell>}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {plan.PromoCodes.map((promo) => (
                                                            <TableRow key={promo.id}>
                                                                <TableCell sx={{ fontFamily: 'monospace' }}>{promo.code}</TableCell>
                                                                <TableCell>
                                                                    {promo.discount_type === 'percentage'
                                                                        ? `-${promo.discount_value}%`
                                                                        : `-${promo.discount_value} ${plan.currency}`}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {promo.starts_at || promo.ends_at
                                                                        ? `${toDateInput(promo.starts_at) || '…'} → ${toDateInput(promo.ends_at) || '…'}`
                                                                        : 'Illimitée'}
                                                                </TableCell>
                                                                <TableCell>{promo.used_count}{promo.max_uses ? ` / ${promo.max_uses}` : ''}</TableCell>
                                                                <TableCell>
                                                                    <Chip label={promo.is_active ? 'Actif' : 'Inactif'} size="small"
                                                                        color={promo.is_active ? 'success' : 'default'} />
                                                                </TableCell>
                                                                {!disabled && (
                                                                    <TableCell>
                                                                        <IconButton size="small" onClick={() => openEditPromo(plan.id, promo)}><EditIcon fontSize="small" /></IconButton>
                                                                        <IconButton size="small" color="error" onClick={() => handleDeletePromo(promo)}><DeleteIcon fontSize="small" /></IconButton>
                                                                    </TableCell>
                                                                )}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">Aucun code promo pour ce plan.</Typography>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Dialog Plan */}
            <Dialog open={planDialogOpen} onClose={() => setPlanDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingPlan ? 'Modifier le plan tarifaire' : 'Nouveau plan tarifaire'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {planFormError && <Grid item xs={12}><Alert severity="error">{planFormError}</Alert></Grid>}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Type de tarif</InputLabel>
                                <Select
                                    value={planForm.type}
                                    label="Type de tarif"
                                    onChange={(e) => setPlanForm(f => ({ ...f, type: e.target.value }))}
                                >
                                    {PRICING_PLAN_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Nom" value={planForm.name}
                                onChange={(e) => setPlanForm(f => ({ ...f, name: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField fullWidth size="small" label="Montant de base" type="number" value={planForm.base_amount}
                                onChange={(e) => setPlanForm(f => ({ ...f, base_amount: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField fullWidth size="small" label="Devise" value={planForm.currency}
                                onChange={(e) => setPlanForm(f => ({ ...f, currency: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Priorité" type="number" value={planForm.priority}
                                onChange={(e) => setPlanForm(f => ({ ...f, priority: e.target.value }))} />
                        </Grid>
                        {/* Pack : quota inclus, rechargé chaque mois quelle que soit la récurrence de paiement */}
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Quota mensuel inclus" type="number" value={planForm.quantity_limit}
                                onChange={(e) => setPlanForm(f => ({ ...f, quantity_limit: e.target.value }))}
                                helperText="Ex: 50000. Vide = pas un pack" />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField fullWidth size="small" label="Unité du quota" value={planForm.dimension}
                                onChange={(e) => setPlanForm(f => ({ ...f, dimension: e.target.value }))}
                                placeholder="transfers"
                                helperText="Celle que l'application consomme" />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField fullWidth size="small" label="Montant annuel" type="number" value={planForm.annual_amount}
                                onChange={(e) => setPlanForm(f => ({ ...f, annual_amount: e.target.value }))}
                                helperText="Montant de base = prix mensuel" />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Début (optionnel)" type="date" InputLabelProps={{ shrink: true }} sx={dateInputSx}
                                value={planForm.starts_at} onChange={(e) => setPlanForm(f => ({ ...f, starts_at: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Fin (optionnel)" type="date" InputLabelProps={{ shrink: true }} sx={dateInputSx}
                                value={planForm.ends_at} onChange={(e) => setPlanForm(f => ({ ...f, ends_at: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={planForm.is_active}
                                    onChange={(e) => setPlanForm(f => ({ ...f, is_active: e.target.checked }))} />}
                                label="Plan actif"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={permissionsCatalog.map(p => p.key)}
                                getOptionLabel={(key) => {
                                    const found = permissionsCatalog.find(p => p.key === key);
                                    return found ? `${found.name} (${found.key})` : key;
                                }}
                                renderOption={(props, key) => {
                                    const found = permissionsCatalog.find(p => p.key === key);
                                    return (
                                        <li {...props} key={key}>
                                            <Box>
                                                <Typography variant="body2">{found ? found.name : key}</Typography>
                                                {found?.description && (
                                                    <Typography variant="caption" color="text.secondary">{found.description}</Typography>
                                                )}
                                            </Box>
                                        </li>
                                    );
                                }}
                                value={planForm.authorizations}
                                onChange={(e, newValue) => setPlanForm(f => ({ ...f, authorizations: newValue }))}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const found = permissionsCatalog.find(p => p.key === option);
                                        return (
                                            <Chip variant="outlined" label={found ? found.name : option} size="small" {...getTagProps({ index })} key={option} />
                                        );
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Permissions accordées par ce plan"
                                        placeholder={permissionsCatalog.length ? 'Choisis dans le catalogue' : 'Aucune permission définie pour cette application (onglet "Permissions")'}
                                        helperText="Droits transférés à client_management (licence du client) au moment de l'octroi, indépendamment du prix payé"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={planForm.authorizations}
                                getOptionLabel={(key) => {
                                    const found = permissionsCatalog.find(p => p.key === key);
                                    return found ? found.name : key;
                                }}
                                value={planForm.highlighted_permissions.filter(key => planForm.authorizations.includes(key))}
                                onChange={(e, newValue) => setPlanForm(f => ({ ...f, highlighted_permissions: newValue }))}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const found = permissionsCatalog.find(p => p.key === option);
                                        return (
                                            <Chip color="primary" label={found ? found.name : option} size="small" {...getTagProps({ index })} key={option} />
                                        );
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Fonctionnalités mises en avant sur la carte du plan"
                                        placeholder={planForm.authorizations.length ? 'Choisis parmi les permissions accordées ci-dessus' : 'Accorde d\'abord des permissions ci-dessus'}
                                        helperText="Les autres permissions accordées restent visibles dans la grille de comparaison complète de l'e-boutique"
                                    />
                                )}
                                disabled={planForm.authorizations.length === 0}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth multiline rows={4} label="Configuration (JSON libre selon le type de tarif)"
                                value={planForm.configText}
                                onChange={(e) => setPlanForm(f => ({ ...f, configText: e.target.value }))}
                                sx={{ fontFamily: 'monospace' }}
                                helperText='Ex: { "discount_percent": 20 } pour une remise, { "billing_period": "monthly" } pour un abonnement...'
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPlanDialogOpen(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleSavePlan} disabled={!planForm.name}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {editingPlan ? 'Modifier' : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Tier */}
            <Dialog open={tierDialogOpen} onClose={() => setTierDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingTier ? 'Modifier le palier' : 'Ajouter un palier'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Dimension (ex: users, storage_gb, months)"
                                value={tierForm.dimension} onChange={(e) => setTierForm(f => ({ ...f, dimension: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Valeur min" type="number" value={tierForm.min_value}
                                onChange={(e) => setTierForm(f => ({ ...f, min_value: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Valeur max (vide = illimité)" type="number" value={tierForm.max_value}
                                onChange={(e) => setTierForm(f => ({ ...f, max_value: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Prix unitaire" type="number" value={tierForm.unit_price}
                                onChange={(e) => setTierForm(f => ({ ...f, unit_price: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Frais fixe (optionnel)" type="number" value={tierForm.flat_fee}
                                onChange={(e) => setTierForm(f => ({ ...f, flat_fee: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Libellé (optionnel)" value={tierForm.label}
                                onChange={(e) => setTierForm(f => ({ ...f, label: e.target.value }))} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTierDialogOpen(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleSaveTier} disabled={tierForm.unit_price === ''}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {editingTier ? 'Modifier' : 'Ajouter'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Promo */}
            <Dialog open={promoDialogOpen} onClose={() => setPromoDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingPromo ? 'Modifier le code promo' : 'Nouveau code promo'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Code" value={promoForm.code}
                                onChange={(e) => setPromoForm(f => ({ ...f, code: e.target.value }))} disabled={!!editingPromo} />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Type de remise</InputLabel>
                                <Select value={promoForm.discount_type} label="Type de remise"
                                    onChange={(e) => setPromoForm(f => ({ ...f, discount_type: e.target.value }))}>
                                    <MenuItem value="percentage">Pourcentage</MenuItem>
                                    <MenuItem value="fixed_amount">Montant fixe</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Valeur" type="number" value={promoForm.discount_value}
                                onChange={(e) => setPromoForm(f => ({ ...f, discount_value: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Début (optionnel)" type="date" InputLabelProps={{ shrink: true }} sx={dateInputSx}
                                value={promoForm.starts_at} onChange={(e) => setPromoForm(f => ({ ...f, starts_at: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Fin (optionnel)" type="date" InputLabelProps={{ shrink: true }} sx={dateInputSx}
                                value={promoForm.ends_at} onChange={(e) => setPromoForm(f => ({ ...f, ends_at: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Usages max (optionnel)" type="number" value={promoForm.max_uses}
                                onChange={(e) => setPromoForm(f => ({ ...f, max_uses: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Usages max / client (optionnel)" type="number" value={promoForm.max_uses_per_user}
                                onChange={(e) => setPromoForm(f => ({ ...f, max_uses_per_user: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={promoForm.is_active}
                                    onChange={(e) => setPromoForm(f => ({ ...f, is_active: e.target.checked }))} />}
                                label="Code actif"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPromoDialogOpen(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleSavePromo} disabled={!promoForm.code || promoForm.discount_value === ''}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {editingPromo ? 'Modifier' : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
